/**
 * The GA4 ecommerce funnel and product page behaviour events. Loaded by sections/main-product.liquid
 * and sections/cart-drawer.liquid; it listens to the theme's own events and never touches the cart
 * or product code (docs/contracts.md, Analytics).
 *
 * - view_item: `product:change` from <product-purchase>, once per variant shown.
 * - add_to_cart / remove_from_cart: `cart:added` and `cart:updated`, diffed against the last cart
 *   seen (first read from the drawer's [data-cart-json]), so a quantity counts what changed.
 * - view_cart: `cart:open` from the drawer. click_checkout: a click inside [data-checkout], the
 *   intent only; begin_checkout belongs to the checkout pixel, which sees every route in.
 * - select_item: a link or button click inside the "Complete the wall" card ([data-upsell]).
 * - select_size, view_gallery_image, open_size_guide, open_accordion: with the product id.
 */
import { googleItem, readConfig, track as send } from '@wispaire/analytics';
import { CART_ADDED, CART_UPDATED } from '@wispaire/global';

/** Fired on document by the cart drawer each time it opens. */
export const CART_OPEN = 'cart:open';
/** Fired by <product-gallery> when a different slide is showing; detail { index, kind }. */
export const GALLERY_CHANGE = 'gallery:change';

const UPSELL_LIST = 'cart_upsell';

/** A GA4 item from a Shopify cart line (the /cart.js shape, prices in cents). */
function lineItem(line) {
  const product = { id: line.product_id, title: line.product_title };
  const variant = { id: line.variant_id, title: line.variant_title, price: line.price };
  return { ...googleItem(product, variant), quantity: line.quantity };
}

/** Lines whose quantity rose or fell between two carts, each carrying the difference. */
function cartChanges(before, after) {
  const old = new Map(before.items.map((line) => [line.key, line.quantity]));
  const added = [];
  const removed = [];
  for (const line of after.items) {
    const delta = line.quantity - (old.get(line.key) ?? 0);
    old.delete(line.key);
    if (delta > 0) added.push({ ...line, quantity: delta });
    if (delta < 0) removed.push({ ...line, quantity: -delta });
  }
  for (const line of before.items) if (old.has(line.key)) removed.push(line);
  return { added, removed };
}

const closest = (event, selector) => event.target?.closest?.(selector) ?? null;
const productId = (event) => closest(event, '[data-product-id]')?.dataset.productId;

/** The cart the page was rendered with, from the drawer markup. */
function readDrawerCart(target) {
  const json = target.querySelector?.('[data-cart-json]')?.textContent;
  return json ? JSON.parse(json) : null;
}

export function connectCommerce({
  target = globalThis.document,
  config = readConfig(),
  track = send,
  readCart = () => readDrawerCart(target),
} = {}) {
  let cart = null;
  const lastCart = () => (cart ??= readCart());

  const sendLines = (name, lines, currency) => {
    if (!lines.length) return;
    track(name, {
      currency: currency ?? config.currency,
      value: lines.reduce((sum, line) => sum + line.price * line.quantity, 0) / 100,
      items: lines.map((line) => ({ ...lineItem(line), currency: currency ?? config.currency })),
    });
  };
  const sendCart = (name) => {
    const current = lastCart();
    if (current) sendLines(name, current.items, current.currency);
  };

  const viewed = new WeakMap();
  const viewItem = (purchase, variant) => {
    if (!purchase || !variant || viewed.get(purchase) === variant.id) return;
    viewed.set(purchase, variant.id);
    const { productId: id, productTitle: title } = purchase.dataset;
    const item = { ...googleItem({ id, title }, variant), currency: config.currency };
    track('view_item', { currency: config.currency, value: item.price, items: [item] });
  };

  target.addEventListener('product:change', (event) => viewItem(event.target, event.detail?.variant));
  // product.js may have chosen the variant before this module loaded.
  for (const purchase of target.querySelectorAll?.('product-purchase') ?? []) viewItem(purchase, purchase.variant);

  target.addEventListener(CART_ADDED, ({ detail = {} }) => {
    const before = lastCart();
    const after = detail.cart;
    // Shopify returns each added line with its new total, so the count comes from the two carts.
    const lines = before && after ? cartChanges(before, after).added : (detail.items ?? []);
    sendLines('add_to_cart', lines, after?.currency);
    cart = after ?? null;
  });

  target.addEventListener(CART_UPDATED, ({ detail = {} }) => {
    const before = lastCart();
    const after = detail.cart;
    if (before && after) {
      const { added, removed } = cartChanges(before, after);
      sendLines('add_to_cart', added, after.currency);
      sendLines('remove_from_cart', removed, after.currency);
    }
    cart = after ?? null;
  });

  target.addEventListener(CART_OPEN, () => sendCart('view_cart'));

  target.addEventListener('click', (event) => {
    if (closest(event, '[data-checkout]')) return sendCart('click_checkout');
    if (closest(event, '[aria-controls="size-in-space"]')) return track('open_size_guide', { product_id: productId(event) });
    const accordion = closest(event, 'button[data-accordion]');
    if (accordion) return track('open_accordion', { product_id: productId(event), accordion: accordion.dataset.accordion });
    // The upsell's card (snippets/card.liquid) carries its GA4 item, as in the lists.
    const card = closest(event, '[data-upsell]') && closest(event, 'a, button') && closest(event, '[data-product-id]');
    if (card) {
      const { productId: id, itemName, variantId, itemVariant, price } = card.dataset;
      const item = googleItem(
        { id, title: itemName },
        { id: variantId, title: itemVariant, price: Number(price) },
        UPSELL_LIST,
        0,
      );
      track('select_item', { item_list_name: UPSELL_LIST, items: [{ ...item, currency: config.currency }] });
    }
  });

  target.addEventListener('change', (event) => {
    if (closest(event, '[data-size-option]')) track('select_size', { product_id: productId(event), size: event.target.value });
  });

  // toggle does not bubble, so it is caught on the way down.
  target.addEventListener(
    'toggle',
    (event) => {
      const details = closest(event, 'details[data-accordion]');
      if (details?.open) track('open_accordion', { product_id: productId(event), accordion: details.dataset.accordion });
    },
    true,
  );

  target.addEventListener(GALLERY_CHANGE, (event) => {
    track('view_gallery_image', { product_id: productId(event), image_index: event.detail.index, image_kind: event.detail.kind });
  });
}

connectCommerce();
